import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, FileSpreadsheet, Loader2, X } from "lucide-react";
import { api } from "../../lib/api";
import { ascii } from "../../lib/contractRegister";
import { lastTakenOverText } from "../../lib/traineeRegister";
import { BTN_PRIMARY, BTN_QUIET } from "./ContractCard";

/**
 * Downloading the trainee register. The file marks what changed since the
 * last download "taken over" — so the choice to tick it is spelled out here:
 * tick it only when you are copying the rows into the official register now.
 */
export default function TraineeRegisterDialog({ group, onClose }) {
  const [status, setStatus] = useState(null);
  const [takenOver, setTakenOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const params = group ? { group_id: group.id } : {};

  useEffect(() => {
    api.get("/admin/contracts/trainee-register/status", { params })
      .then((r) => setStatus(r.data)).catch(() => setStatus({ error: true }));
  }, [group?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && !busy) onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, busy]);

  const download = async () => {
    setBusy(true);
    try {
      const r = await api.get("/admin/contracts/trainee-register.xlsx", {
        params: { ...params, ...(takenOver ? { taken_over: true } : {}) }, responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      const day = new Date().toISOString().slice(0, 10);
      a.href = url; a.download = `registru-formabili-${group ? `${ascii(group.name)}-` : ""}${day}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
      toast.success(takenOver ? "Registrul a fost descărcat și marcat ca preluat." : "Registrul a fost descărcat.");
      onClose();
    } catch {
      toast.error("Registrul formabililor nu a putut fi descărcat.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm"
      onClick={() => !busy && onClose()} data-testid="trainee-register-dialog">
      <div role="dialog" aria-modal="true" aria-label="Registru formabili" onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg rounded-2xl bg-white p-5 shadow-2xl">
        <div className="flex items-start justify-between gap-3">
          <h2 className="text-base font-semibold text-[#373A45]">
            Registru formabili{group ? ` · ${group.name}` : " · toate grupele"}
          </h2>
          <button type="button" onClick={onClose} disabled={busy} className="text-[#6B7280] hover:text-[#373A45]" aria-label="Închide">
            <X size={18} />
          </button>
        </div>
        <p className="mt-1 text-xs leading-relaxed text-[#6B7280]">
          Cei cu plata confirmată, dosarul MEC complet și contractul semnat, plus foaia „Neterminați”.
          Rândurile noi sunt verzi, celulele modificate galbene, iar cei ieșiți după semnare gri — față de ultima
          descărcare preluată. Foaia „Modificări” le spune în cuvinte.
        </p>

        <p className="mt-3 rounded-lg bg-[#F5F6F6] px-3 py-2 text-xs text-[#373A45]" data-testid="trainee-register-status">
          {!status ? "Se verifică…" : status.error ? "Nu am putut citi ultima preluare." : lastTakenOverText(status)}
        </p>

        <label className="mt-4 flex cursor-pointer items-start gap-3 rounded-xl border border-[#E5E7EB] p-3 hover:bg-[#F5F6F6]">
          <input type="checkbox" checked={takenOver} onChange={(e) => setTakenOver(e.target.checked)}
            className="mt-0.5 h-4 w-4 shrink-0 accent-[#965EA7]" data-testid="trainee-register-taken-over" />
          <span className="text-sm text-[#373A45]">
            <strong>Marchează ca preluat în registru</strong>
            <span className="mt-1 block text-xs leading-relaxed text-[#6B7280]">
              Bifează doar dacă treci <strong>acum</strong> rândurile în registrul oficial. Descărcarea aceasta devine
              reperul: data viitoare, fișierul va marca doar ce s-a schimbat după ea. Dacă descarci doar ca să te uiți,
              lasă nebifat — altfel colegii nu vor mai vedea ca noi rândurile pe care încă nu le-au preluat.
            </span>
          </span>
        </label>

        <p className="mt-3 flex items-start gap-1.5 text-xs text-[#8a6a1a]">
          <AlertTriangle size={13} className="mt-0.5 shrink-0" />
          Fișierul conține CNP-urile formabililor. Descărcarea rămâne în jurnalul de audit.
        </p>

        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} disabled={busy} className={BTN_QUIET}>Renunță</button>
          <button type="button" onClick={download} disabled={busy} className={BTN_PRIMARY} data-testid="trainee-register-download">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <FileSpreadsheet size={14} />}
            {takenOver ? "Descarcă și marchează preluat" : "Descarcă"}
          </button>
        </div>
      </div>
    </div>
  );
}
